import React, { useEffect } from "react";
import ReactDOM from "react-dom";
//#region types
import { TwoDChartProps } from "models/propTypes/chartProps";
import { GraphConstants } from "utils/constants";
import { ChartEnums } from "models/enums";
import { Moment } from "moment";
//#endregion
//#region components
import Chart, { ChartDataSets, ChartPoint } from "chart.js";
import { Grid } from "@mui/material";
//#endregion
import variables from "theme/styles/_variables.module.scss";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";
import { ChartHelper, LocalizationHelperInstance } from "utils/helpers";
import useChartContext from "./context/useContext";
import { createFreezeTooltipPlugin } from "./utils/freezeTooltipPlugin";
import { registerOutsideClickClear } from "./utils/outsideClick";

const BarChart: React.FC<TwoDChartProps> = (props) => {
  const chartCanvasId = `${GraphConstants.BarChartIdPrefix}${props.id}`;
  const chartLegendId = `${GraphConstants.LegendIdPrefix}${GraphConstants.BarChartIdPrefix}${props.id}`;
  let legendPortalContainer = props.legendPortalId ? document.getElementById(props.legendPortalId) : null;

  const radius = 30;
  const borderWidth = 1;
  const selectedBarIndexRef = React.useRef<number | null>(null);
  const originalBackgroundRef = React.useRef<any[] | null>(null); // Now stores {backgroundColor, borderColor} objects
  const chartRef = React.useRef<any>(null);
  const frozenRef = React.useRef<boolean>(false);
  const selectedDatasetIndexRef = React.useRef<number | null>(null);
  const justSetSelectionRef = React.useRef<number | null>(null);

  const chartContext = useChartContext();

  React.useEffect(() => {
    if (legendPortalContainer) {
      if (legendPortalContainer.childElementCount > 1 && legendPortalContainer.firstChild)
        legendPortalContainer.removeChild(legendPortalContainer.firstChild);
    }

    return () => {
      legendPortalContainer = null;
    };
  }, []);

  const getFreezePlugin = () =>
    createFreezeTooltipPlugin({
      id: `${chartCanvasId}-freeze`,
      canvasId: chartCanvasId,
      isFrozenRef: frozenRef,
      hasSelectionRef: selectedBarIndexRef as any,
      getActiveElement: (chart: any) => {
        const dsi = selectedDatasetIndexRef.current ?? 0;
        return chart.getDatasetMeta(dsi)?.data?.[selectedBarIndexRef.current as any];
      },
      withBeforeUpdate: true,
      withAfterDraw: true,
      drawOnAfterEvent: true,
    });

  React.useEffect(() => {
    const freezePluginId = `${chartCanvasId}-freeze`;
    try {
      const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
      existing && Chart.pluginService.unregister(existing);
    } catch {}
    try {
      Chart.pluginService.register(getFreezePlugin());
    } catch {}
    return () => {
      try {
        const existing = Chart.pluginService.getAll().find((c) => c.id === freezePluginId);
        existing && Chart.pluginService.unregister(existing);
      } catch {}
    };
  }, [chartCanvasId]);

  useEffect(() => {
    let ctx: any = document.getElementById(chartCanvasId);
    const twoDContext = ctx.getContext("2d");
    twoDContext.fillRect(0, 0, 10, 200);

    let roundedCornerBar = Chart.controllers.bar.extend({
      draw: function (ease: any) {
        Chart.controllers.bar.prototype.draw.call(this, ease);
        const ctx = this.chart.chart.ctx;

        const bars = this.getMeta().data;

        bars.forEach((bar: any) => {
          const view = bar._view;
          if (view.y !== view.base) {
            let radius;
            const borderWidth = view.borderWidth;
            const borderColor = view.borderColor;

            radius = 30;

            const left = view.x - view.width / 2;
            const right = view.x + view.width / 2;

            const top = view.y;
            const bottom = view.base;

            const sign = bottom > top ? 1 : -1;

            ctx.beginPath();

            ctx.fillStyle = view.backgroundColor;
            ctx.lineWidth = borderWidth;
            ctx.strokeStyle = borderColor || variables.blueV4;

            const width = right - left;
            const height = sign * (bottom - top);

            if (radius > height) {
              radius = height;
            }
            if (radius > width / 2) {
              radius = width / 2;
            }

            if (bottom < top) {
              ctx.clearRect(left - borderWidth, bottom + borderWidth, view.width + 2 * borderWidth, height);
              ctx.moveTo(left, bottom + borderWidth);
              ctx.lineTo(left, top - radius);
              ctx.quadraticCurveTo(left, top, left + radius, top);
              ctx.lineTo(right - radius, top);
              ctx.quadraticCurveTo(right, top, right, top - radius);
              ctx.lineTo(right, bottom + borderWidth);
              ctx.lineTo(left, bottom + borderWidth);
            } else {
              ctx.clearRect(left - borderWidth, top - borderWidth, view.width + 2 * borderWidth, height);
              ctx.moveTo(left, bottom - borderWidth);
              ctx.lineTo(left, top + radius);
              ctx.quadraticCurveTo(left, top, left + radius, top);
              ctx.lineTo(right - radius, top);
              ctx.quadraticCurveTo(right, top, right, top + radius);
              ctx.lineTo(right, bottom - borderWidth);
              ctx.lineTo(left, bottom - borderWidth);
            }

            ctx.closePath();

            ctx.fill();
            if (borderWidth) {
              ctx.stroke();
            }
          }
        });
      },
    });

    Chart.controllers.roundedCornerBar = roundedCornerBar;

    const chart = new Chart(ctx, {
      type: "roundedCornerBar",
      data: {
        labels: props.labels,
        datasets: getData(twoDContext),
      },
      options: {
        responsive: true,
        maintainAspectRatio: props.maintainAspectRatio ?? false,
        legend: {
          display: false,
        },
        legendCallback: function (chart) {
          return ChartHelper.generateLegend(chart, "background");
        },
        layout: {
          padding: {
            left: -5,
            right: 8,
          },
        },
        onHover: function (event, activeElements) {
          ctx.style.cursor = activeElements[0] ? "pointer" : "default";
        },
        onClick: function (event, activeElements) {
          if (!props.useClickableChart) return;
          if (activeElements !== undefined && activeElements[0]) {
            const datasetIndex = (activeElements[0] as any)._datasetIndex;
            const chartData = chart.config.data;
            const index = (activeElements[0] as any)._index;

            const label = chartData?.labels !== undefined ? chartData.labels[index] : undefined;

            const dataSet = chartData?.datasets !== undefined ? chartData.datasets[datasetIndex] : undefined;

            const xAxisPoint = (dataSet?.data !== undefined && dataSet?.data !== null) ? (dataSet.data[index] as Chart.ChartPoint)?.x : undefined;

            try {
              try {
                chartContext?.setSelectedChartId && chartContext.setSelectedChartId(String(props.id));
              } catch {}
              selectedBarIndexRef.current = index;
              selectedDatasetIndexRef.current = datasetIndex;
              const datasets = chart.data?.datasets ?? [];
              if (!originalBackgroundRef.current) {
                originalBackgroundRef.current = datasets.map((ds) => ({
                  backgroundColor: ds.backgroundColor,
                  borderColor: ds.borderColor,
                }));
              }
              datasets.forEach((ds, dsi) => {
                const baseData = originalBackgroundRef.current
                  ? originalBackgroundRef.current[dsi]
                  : {
                      backgroundColor: ds.backgroundColor,
                      borderColor: ds.borderColor,
                    };
                const baseBg = baseData.backgroundColor || baseData;
                const baseBorder = baseData.borderColor;

                if (Array.isArray(baseBg)) {
                  ds.backgroundColor = (baseBg as any[]).map((c, i) =>
                    // Only keep the specific bar (matching both dataset and index) fully opaque
                    dsi === datasetIndex && i === index ? c : (Chart as any).helpers.color(c).alpha(0.3).rgbString()
                  );
                } else if (typeof baseBg === "string") {
                  const values = (ds.data as any[]) ?? [];
                  ds.backgroundColor = values.map((_, i) =>
                    // Only keep the specific bar (matching both dataset and index) fully opaque
                    dsi === datasetIndex && i === index
                      ? (baseBg as string)
                      : (Chart as any).helpers.color(baseBg).alpha(0.3).rgbString()
                  );
                }

                // Apply same opacity to borders
                if (baseBorder) {
                  if (Array.isArray(baseBorder)) {
                    ds.borderColor = (baseBorder as any[]).map((c, i) =>
                      dsi === datasetIndex && i === index ? c : (Chart as any).helpers.color(c).alpha(0.3).rgbString()
                    );
                  } else if (typeof baseBorder === "string") {
                    const values = (ds.data as any[]) ?? [];
                    ds.borderColor = values.map((_, i) =>
                      dsi === datasetIndex && i === index
                        ? (baseBorder as string)
                        : (Chart as any).helpers.color(baseBorder).alpha(0.3).rgbString()
                    );
                  }
                }
              });

              try {
                chartContext?.setSelection && chartContext.setSelection(String(props.id), { index, datasetIndex });
              } catch {}
              frozenRef.current = true;
              try {
                const activeBar = chart.getDatasetMeta(datasetIndex)?.data?.[index];
                if (activeBar) {
                  (chart as any).tooltip._active = [activeBar];
                  (chart as any).tooltip.update(true);
                }
              } catch {}
              chart.update();
              justSetSelectionRef.current = Date.now();
            } catch {}

            if (props.onClick) props.onClick(index, datasetIndex, xAxisPoint, label);
          }
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontColor: GraphConstants.BarLegendFontColor,
                fontSize: props.overrideLabelFontSize ?? 12,
                fontFamily: "MuseoSans-700",
                padding: 8,
                maxTicksLimit: 4,
                callback: function (value, index, values) {
                  if (props.yAxisTickUnit === ChartEnums.YAxisTickUnitType.Currency) {
                    if (typeof value === "number") {
                      return ChartHelper.validateFormatChartYAxis(value, values);
                    }
                  }
                  return value;
                },
              },
              gridLines: {
                borderDash: [3, 2],
                drawBorder: false,
                color: GraphConstants.BarGridLinesColor,
                zeroLineColor: GraphConstants.BarAxisColor,
                zeroLineWidth: 2,
                lineWidth: 1,
              },
            },
          ],
          xAxes: [
            {
              labels: ChartHelper.is2DArray(props.labels)
                ? ChartHelper.getLableFor2DArray((props.labels ?? []) as (string[] | number[] | Moment[] | Date[])[])
                : ChartHelper.getLableForArray((props.labels ?? []) as (string | number | Moment | Date)[]),
              ticks: {
                autoSkip: true,
                autoSkipPadding: 10,
                backdropColor: GraphConstants.BarBackdropColor,
                fontColor: GraphConstants.BarLegendFontColor,
                fontSize: props.overrideLabelFontSize ?? 12,
                fontFamily: "MuseoSans-700",
                maxRotation: 0,
                callback: ChartHelper.getDisplayLabel,
              },
              distribution: "linear",
              gridLines: {
                display: false,
                color: GraphConstants.BarGridLinesColor,
                tickMarkLength: 8,
              },
              type: "category",
              offset: true,
            },
          ],
        },
        elements: {
          point: {
            pointStyle: "circle",
          },
        },
        tooltips: {
          backgroundColor: GraphConstants.TooltipBackgroundColor,
          titleFontColor: GraphConstants.TooltipTitleFontColor,
          titleFontSize: GraphConstants.TooltipTitleFontSize,
          bodyFontColor: GraphConstants.TooltipBodyFontColor,
          bodyFontSize: GraphConstants.TooltipBodyFontSize,
          displayColors: false,

          callbacks: {
            title: function (tooltipItems, data) {
              if (tooltipItems === undefined || data === undefined) {
                return "";
              }

              const xAxisIndex = tooltipItems[0]["index"];
              const dataSetIndex = tooltipItems[0]["datasetIndex"];

              const is2DArray = ChartHelper.is2DArray(data["labels"]);
              const toolTips = props.tooltips
                ? is2DArray
                  ? props.tooltips[dataSetIndex ?? 0]
                  : props.tooltips
                : data["labels"];

              if (toolTips !== undefined && xAxisIndex !== undefined) {
                return toolTips[xAxisIndex].toString();
              } else return "";
            },

            label: function (tooltipItem, data) {
              const index = tooltipItem.index ?? 0;
              const datasetIndex = tooltipItem.datasetIndex ?? 0;
              // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
              const dataset = data?.datasets != undefined ? data.datasets[datasetIndex] : undefined;

              const chartPoint =
                // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
                index != undefined && dataset != undefined && dataset.data != undefined ? dataset.data[index] : 0;

              const y = (chartPoint as ChartPoint).y;
              return `${
                typeof y === "number" ? LocalizationHelperInstance.getFormattedNumber(y ?? 0, 0, 2) : y?.toString()
              } ${props.dataUnit ?? GraphConstants.DefaultDataUnit}`;
            },
          },
        },
      },
    });

    configureLegendClick(
      (legendItem: HTMLLIElement) => legendItem.addEventListener("click", legendClickCallback, false),
      chart.generateLegend() as string
    );

    // snapshot original backgrounds and borders on initial render
    try {
      const datasets = chart.data?.datasets ?? [];
      originalBackgroundRef.current = datasets.map((ds) => ({
        backgroundColor: ds.backgroundColor,
        borderColor: ds.borderColor,
      }));
    } catch {}

    // Re-apply selection dimming if stored in context
    try {
      const stored = chartContext?.getSelection ? chartContext.getSelection(String(props.id)) : null;
      if (stored != null) {
        const storedIndex = stored.index;
        const storedDatasetIndex = stored.datasetIndex ?? 0;
        selectedBarIndexRef.current = storedIndex;
        selectedDatasetIndexRef.current = storedDatasetIndex;
        const datasets = chart.data?.datasets ?? [];
        datasets.forEach((ds, dsi) => {
          const baseData = originalBackgroundRef.current
            ? originalBackgroundRef.current[dsi]
            : {
                backgroundColor: ds.backgroundColor,
                borderColor: ds.borderColor,
              };
          const baseBg = baseData.backgroundColor || baseData;
          const baseBorder = baseData.borderColor;

          if (Array.isArray(baseBg)) {
            ds.backgroundColor = (baseBg as any[]).map((c, i) =>
              // Only keep the specific bar (matching both dataset and index) fully opaque
              dsi === storedDatasetIndex && i === storedIndex
                ? c
                : (Chart as any).helpers.color(c).alpha(0.3).rgbString()
            );
          } else if (typeof baseBg === "string") {
            const values = (ds.data as any[]) ?? [];
            ds.backgroundColor = values.map((_, i) =>
              // Only keep the specific bar (matching both dataset and index) fully opaque
              dsi === storedDatasetIndex && i === storedIndex
                ? (baseBg as string)
                : (Chart as any).helpers.color(baseBg).alpha(0.3).rgbString()
            );
          }

          // Apply same opacity to borders
          if (baseBorder) {
            if (Array.isArray(baseBorder)) {
              ds.borderColor = (baseBorder as any[]).map((c, i) =>
                dsi === storedDatasetIndex && i === storedIndex
                  ? c
                  : (Chart as any).helpers.color(c).alpha(0.3).rgbString()
              );
            } else if (typeof baseBorder === "string") {
              const values = (ds.data as any[]) ?? [];
              ds.borderColor = values.map((_, i) =>
                dsi === storedDatasetIndex && i === storedIndex
                  ? (baseBorder as string)
                  : (Chart as any).helpers.color(baseBorder).alpha(0.3).rgbString()
              );
            }
          }
        });
        frozenRef.current = true;
        try {
          const activeBar = chart.getDatasetMeta(selectedDatasetIndexRef.current ?? 0)?.data?.[storedIndex];
          if (activeBar) {
            (chart as any).tooltip._active = [activeBar];
            (chart as any).tooltip.update(true);
          }
        } catch {}
        chart.update();
      }
    } catch {}

    chartRef.current = chart;

    return () => {
      ctx = null;
      chart.destroy();
      roundedCornerBar = null;
      configureLegendClick((legendItem: HTMLLIElement) => legendItem.removeEventListener("click", legendClickCallback));
    };
  }, [props]);

  React.useEffect(() => {
    const selected = chartContext?.selectedChartId;
    // If selection is cleared globally (e.g., Clear filter), clear this chart immediately
    if (!selected) {
      if (chartRef.current) {
        try {
          const chart = chartRef.current as any;
          const datasets = chart.data?.datasets ?? [];
          if (originalBackgroundRef.current) {
            datasets.forEach((ds: any, dsi: number) => {
              const baseData = originalBackgroundRef.current ? originalBackgroundRef.current[dsi] : null;
              if (baseData) {
                ds.backgroundColor = baseData.backgroundColor || baseData;
                if (baseData.borderColor) {
                  ds.borderColor = baseData.borderColor;
                }
              }
            });
          }
          selectedBarIndexRef.current = null;
          selectedDatasetIndexRef.current = null;
          frozenRef.current = false;
          justSetSelectionRef.current = null;
          try {
            chart.tooltip._active = [];
            chart.tooltip.update(true);
          } catch {}
          try {
            chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
          } catch {}
          chart.update();
        } catch {}
      }
      return;
    }
    if (selected === props.id) return;
    // If this chart was just clicked (grace period), ignore immediate context changes from other charts
    try {
      const now = Date.now();
      const lastSetAt = justSetSelectionRef.current;
      const withinGrace = lastSetAt != null && now - lastSetAt < 600;
      const hasStored = !!(chartContext?.getSelection && chartContext.getSelection(String(props.id)));
      if (withinGrace && hasStored && frozenRef.current && selectedBarIndexRef.current != null) {
        return;
      }
    } catch {}
    if (chartRef.current) {
      try {
        const chart = chartRef.current as any;
        const datasets = chart.data?.datasets ?? [];
        if (originalBackgroundRef.current) {
          datasets.forEach((ds: any, dsi: number) => {
            const baseData = originalBackgroundRef.current ? originalBackgroundRef.current[dsi] : null;
            if (baseData) {
              ds.backgroundColor = baseData.backgroundColor || baseData;
              if (baseData.borderColor) {
                ds.borderColor = baseData.borderColor;
              }
            }
          });
        }
        selectedBarIndexRef.current = null;
        selectedDatasetIndexRef.current = null;
        frozenRef.current = false;
        justSetSelectionRef.current = null;
        try {
          chart.tooltip._active = [];
          chart.tooltip.update(true);
        } catch {}
        try {
          chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
        } catch {}
        chart.update();
      } catch {}
    }
  }, [chartContext?.selectedChartId, props.id]);

  React.useEffect(() => {
    const unregister = registerOutsideClickClear({
      canvasId: chartCanvasId,
      legendId: chartLegendId,
      resolvePanelRoot: (canvas) => {
        const root =
          canvas && canvas.closest ? (canvas.closest(`.${cssExports.viewPanelOuter}`) as HTMLElement | null) : null;
        return (root ? root.parentElement : null) as HTMLElement | null;
      },
      insideCanvasHandling: "background-only",
      getChart: () => chartRef.current,
      shouldSkip: () => !props.useClickableChart || (!frozenRef.current && selectedBarIndexRef.current == null),
      onClear: () => {
        if (chartRef.current) {
          try {
            const chart = chartRef.current as any;
            const datasets = chart.data?.datasets ?? [];
            if (originalBackgroundRef.current) {
              datasets.forEach((ds: any, dsi: number) => {
                const baseData = originalBackgroundRef.current ? originalBackgroundRef.current[dsi] : null;
                if (baseData) {
                  ds.backgroundColor = baseData.backgroundColor || baseData;
                  if (baseData.borderColor) {
                    ds.borderColor = baseData.borderColor;
                  }
                }
              });
            }
            selectedBarIndexRef.current = null;
            selectedDatasetIndexRef.current = null;
            frozenRef.current = false;
            try {
              chart.tooltip._active = [];
              chart.tooltip.update(true);
            } catch {}
            try {
              chartContext?.setSelection && chartContext.setSelection(String(props.id), null);
            } catch {}
            chart.update();
            try {
              if (props.onBackgroundClick) props.onBackgroundClick();
            } catch {}
            try {
              chartContext?.setSelectedChartId && chartContext.setSelectedChartId(null);
            } catch {}
          } catch {}
        }
      },
    });
    return unregister;
  }, [chartCanvasId, chartLegendId, chartContext?.setSelectedChartId]);

  const configureLegendClick = (callback: (legendItems: HTMLLIElement) => void, innerHTML?: string) => {
    const myLegendContainer = document.getElementById(chartLegendId);
    // generate HTML legend
    if (myLegendContainer != null && innerHTML) myLegendContainer.innerHTML = innerHTML;

    const legendItems = myLegendContainer?.getElementsByTagName("li");
    // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
    if (legendItems != undefined && legendItems.length > 1)
      for (let i = 0; i < legendItems.length; i += 1) {
        callback(legendItems[i]);
      }
  };

  const getData = (twoDContext: any) => {
    const chartDataSets: ChartDataSets[] = [];
    const colors = GraphConstants.BarChartDefaultColorPalette;

    props.datasets?.forEach((dataset, index) => {
      const colorsForDataset = colors[index];
      const backgroundImageSource = colorsForDataset.backgroundImageSource;
      let fillPattern;

      if (backgroundImageSource) {
        const img = new Image();
        img.src = backgroundImageSource;
        // eslint-disable-next-line eqeqeq --- we might be checking against null and undefined, disabled for now to avoid regression testing once changed
        fillPattern = backgroundImageSource != undefined ? twoDContext.createPattern(img, "repeat") : undefined;
      }

      chartDataSets.push({
        label: ChartHelper.getLabel(dataset.label),
        barPercentage: 0.8,
        radius: radius,
        data: typeof dataset.data === "number" ? undefined : dataset.data,
        backgroundColor: fillPattern ?? colorsForDataset.backgroundColor,
        borderColor: colorsForDataset.borderColor,
        borderWidth: borderWidth,
        categoryPercentage: 0.6,
      });
    });

    return chartDataSets;
  };

  function legendClickCallback(event: MouseEvent) {
    ChartHelper.legendClickCallback(chartCanvasId, event.target as HTMLLIElement);
  }

  function getLegendContainer() {
    if (legendPortalContainer) {
      return ReactDOM.createPortal(
        <Grid
          container
          id={chartLegendId}
          className={clsx(props.classes?.legend, cssExports.chartLegendPortal)}
          justifyContent="center"
        ></Grid>,
        legendPortalContainer
      );
    } else {
      return <Grid container id={chartLegendId} className={clsx(props.classes?.legend)} justifyContent="center"></Grid>;
    }
  }

  return (
    <Grid container className={clsx(props.classes?.canvas, cssExports.chartComponentContainer)}>
      {props.title && (
        <Grid container justifyContent="center" className={cssExports.barChartTitle}>
          {props.title}
        </Grid>
      )}
      <Grid container className={props.classes?.chartContainer}>
        <canvas id={chartCanvasId} />
      </Grid>
      <>{!props.hideLegend && getLegendContainer()}</>
    </Grid>
  );
};

export default BarChart;
